/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { FormGrid } from '../../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../../styles/forms/Field';
import {
   createOrigin,
   updateOrigin,
} from '../../../../../services/hr/recruitment/originsApi';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Criar/renomear uma origem de candidatura. Só o nome é editável aqui; ativar/
// inativar é feito direto na linha do catálogo. Trata unicidade de nome (409).
// ---------------------------------------------------------------------------

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

export default function OriginFormModal({ isOpen, onClose, origin, onSaved }) {
   const isEdit = Boolean(origin);
   const [name, setName] = useState('');
   const [fieldError, setFieldError] = useState(null);
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen) {
         setName(origin?.name ?? '');
         setFieldError(null);
         setIsSubmitting(false);
      }
   }, [isOpen, origin]);

   const handleConfirm = async () => {
      if (isSubmitting) return;
      if (!name.trim()) {
         setFieldError('Informe o nome da origem.');
         return;
      }

      setIsSubmitting(true);
      setFieldError(null);
      try {
         if (isEdit) {
            const { message } = await updateOrigin(origin.id, {
               name: name.trim(),
            });
            toast.success(message);
         } else {
            const { message } = await createOrigin({ name: name.trim() });
            toast.success(message);
         }
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (fieldErrors.name) {
            setFieldError(fieldErrors.name);
         } else {
            toast.error(recruitmentErrorMessage(err, 'Erro ao salvar a origem.'));
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
         >
            {isSubmitting ? 'Salvando…' : 'Salvar'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={isEdit ? 'Editar origem' : 'Nova origem'}
         maxWidth="440px"
         footer={footer}
      >
         <FormGrid>
            <Field className="col-12">
               <label htmlFor="origin-name">
                  Nome
                  <RequiredMark>*</RequiredMark>
               </label>
               <input
                  id="origin-name"
                  type="text"
                  maxLength={80}
                  value={name}
                  onChange={(e) => {
                     setName(e.target.value);
                     setFieldError(null);
                  }}
                  disabled={isSubmitting}
                  aria-invalid={!!fieldError || undefined}
               />
               {fieldError && <FieldError>{fieldError}</FieldError>}
            </Field>
         </FormGrid>
      </Modal>
   );
}

OriginFormModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   origin: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
   }),
   onSaved: PropTypes.func,
};
